/**
 * BUD-15: client-side Content Hash Key (CHK) encryption, format `chk-v1`.
 *
 * The trick is that the key comes from the plaintext:
 *
 *   chk_key   = SHA256(plaintext)
 *   aes_key   = HKDF-SHA256(ikm = chk_key, salt = "hashtree-chk", info = "encryption-key", 32)
 *   ciphertext = AES-256-GCM(aes_key, nonce = 12 zero bytes, plaintext) || 16 byte tag
 *   blob_hash = SHA256(ciphertext)
 *
 * So the same plaintext always yields the same ciphertext and the same Blossom
 * hash, which keeps dedup, caching and quota accounting working on encrypted
 * blobs. The zero nonce is only safe *because* the key is derived from the
 * plaintext, so one key never encrypts two different plaintexts.
 */

import { bytesEqual, sha256, toHex } from "./bytes";

export const CHK_SALT = "hashtree-chk";
export const CHK_INFO = "encryption-key";
export const CHK_NONCE = new Uint8Array(12); // 12 zero bytes
export const CHK_ENC_LABEL = "chk-v1";

export interface EncryptedBlob {
  /** Bytes before encryption. */
  plaintext: Uint8Array;
  /** SHA256(plaintext). The decryption key, and a bearer secret. */
  chkKey: Uint8Array;
  /** What actually gets uploaded: AES-GCM output including the 16 byte tag. */
  ciphertext: Uint8Array;
  /** SHA256(ciphertext). The Blossom hash the blob is addressed by. */
  blobHash: Uint8Array;
}

const textEncoder = new TextEncoder();

/** Step 2: stretch the content hash into an AES key. */
async function deriveAesKey(chkKey: Uint8Array, usage: KeyUsage[]): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey("raw", chkKey.slice(), "HKDF", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: textEncoder.encode(CHK_SALT),
      info: textEncoder.encode(CHK_INFO),
    },
    ikm,
    256,
  );
  return crypto.subtle.importKey("raw", bits, "AES-GCM", false, usage);
}

/** Encrypt one plaintext into an addressable Blossom blob. */
export async function encryptCHK(plaintext: Uint8Array): Promise<EncryptedBlob> {
  const chkKey = await sha256(plaintext);
  const aesKey = await deriveAesKey(chkKey, ["encrypt"]);
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: CHK_NONCE, tagLength: 128 },
    aesKey,
    plaintext.slice(),
  );
  const ciphertext = new Uint8Array(encrypted);
  const blobHash = await sha256(ciphertext);
  return { plaintext, chkKey, ciphertext, blobHash };
}

/**
 * Decrypt a blob fetched from a server.
 *
 * All four checks are security-critical. BUD-15 is explicit that clients MUST
 * NOT skip the hash verifications as an optimization: without them a server
 * could hand back different bytes than the ones the link committed to.
 */
export async function decryptCHK(
  ciphertext: Uint8Array,
  chkKey: Uint8Array,
  expectedBlobHash: Uint8Array,
): Promise<Uint8Array> {
  // 1. Verify SHA256(ciphertext) == blob_hash.
  const actualBlobHash = await sha256(ciphertext);
  if (!bytesEqual(actualBlobHash, expectedBlobHash)) {
    throw new Error(`blob hash mismatch: expected ${toHex(expectedBlobHash)}, got ${toHex(actualBlobHash)}`);
  }

  // 2. Derive the AES key from chk_key.
  const aesKey = await deriveAesKey(chkKey, ["decrypt"]);

  // 3. AES-GCM decrypt with the zero nonce. Throws if the tag does not verify.
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: CHK_NONCE, tagLength: 128 },
    aesKey,
    ciphertext.slice(),
  );
  const plaintext = new Uint8Array(decrypted);

  // 4. Verify SHA256(plaintext) == chk_key.
  const actualChkKey = await sha256(plaintext);
  if (!bytesEqual(actualChkKey, chkKey)) {
    throw new Error(`chk key mismatch: expected ${toHex(chkKey)}, got ${toHex(actualChkKey)}`);
  }

  return plaintext;
}

/**
 * A `blossom:` URI for a CHK encrypted blob (BUD-10 + BUD-15).
 *
 * `k` is a bearer secret, so this URI must never be handed to a Blossom server;
 * it is for sharing between clients.
 */
export function chkBlossomUri(blobHash: Uint8Array, chkKey: Uint8Array, extension?: string): string {
  const suffix = extension ? `.${extension.replace(/^\./, "")}` : "";
  return `blossom:${toHex(blobHash)}${suffix}?enc=${CHK_ENC_LABEL}&k=${toHex(chkKey)}`;
}
